import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Plus, Printer, Tv, ClipboardList, ListChecks, Search, Clock, User, Car, Wrench, Shield, PaintBucket, X, FileText, Trash2, ChevronRight, MessageCircle, CarFront, UserPlus, Paperclip, Upload, Smartphone, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { CHECKLIST_ITEMS } from '@/components/workorder/constants';
import { useTranslation } from 'react-i18next';
import { WorkshopTaskDialog } from '@/components/agenda/WorkshopTaskDialog';
import { AgendaEventContent } from '@/components/agenda/AgendaEventContent';
import { todayISO } from '@/components/agenda/agendaUtils';
import { BookingPanel } from '@/components/agenda/booking/BookingPanel';
import { WorkshopSettingsDialog } from '@/components/agenda/booking/WorkshopSettingsDialog';
import { AppointmentStatusBox } from '@/components/agenda/booking/AppointmentStatusBox';
import { CheckInDialog } from '@/components/agenda/booking/CheckInDialog';
import { CheckOutDialog } from '@/components/agenda/booking/CheckOutDialog';

const TIME_SLOTS = [];
for (let h = 7; h <= 19; h++) {
  for (let m = 0; m < 60; m += 30) {
    if (h === 19 && m > 0) break;
    TIME_SLOTS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

const CATEGORY_COLORS = {
  mecanique: { bg: '#3b82f6', border: '#2563eb', label: 'Mecanique' },
  carrosserie: { bg: '#f59e0b', border: '#d97706', label: 'Carrosserie' },
  assurance: { bg: '#8b5cf6', border: '#7c3aed', label: 'Assurance' },
  rdv: { bg: '#0d9488', border: '#0f766e', label: 'Rendez-vous' },
  interne: { bg: '#16a34a', border: '#15803d', label: 'Travaux internes' },
};

const STATUS_LABELS = {
  scheduled: 'Planifie',
  in_progress: 'En cours',
  completed: 'Termine',
  cancelled: 'Annule',
};

const AgendaPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [form, setForm] = useState({
    client_id: '', vehicle_id: '', date: '', time: '08:00',
    end_date: '', end_time: '', pickup_date: '', pickup_time: '',
    drop_off_time: '', category: 'mecanique', intervention_type: '',
    description: '', insurance_number: '',
    loaner_vehicle: false, loaner_vehicle_rate: '', checklist: {},
    sinistre_numbers: ['', '', ''],
    custom_reminder: null,
  });
  const [clientSearch, setClientSearch] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editingAppt, setEditingAppt] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [scanSession, setScanSession] = useState(null);
  const scanPollRef = useRef(null);
  const calendarRef = useRef(null);
  const [tasks, setTasks] = useState([]);
  const [taskDialog, setTaskDialog] = useState(null);
  const [viewDate, setViewDate] = useState(todayISO());
  const [booking, setBooking] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => { loadAppointments(); loadClients(); loadTasks(); }, []);

  const loadTasks = async () => {
    try {
      const res = await axios.get(`${API}/agenda-tasks`);
      setTasks(res.data);
    } catch (err) { console.error(err); }
  };

  // Handle return from client creation
  useEffect(() => {
    const newClientId = searchParams.get('newClientId');
    if (newClientId && clients.length > 0) {
      const client = clients.find(c => c.id === newClientId);
      if (client) {
        // Restore saved form data
        const savedForm = localStorage.getItem('agenda_pending_form');
        if (savedForm) {
          try {
            const parsed = JSON.parse(savedForm);
            setForm(f => ({ ...f, ...parsed, client_id: client.id }));
          } catch { /* ignore */ }
          localStorage.removeItem('agenda_pending_form');
        }
        setSelectedClient(client);
        setForm(f => ({ ...f, client_id: client.id }));
        loadVehiclesForClient(client.id);
        setShowDialog(true);
      }
      // Clean URL
      setSearchParams({}, { replace: true });
    }
  }, [clients, searchParams]);

  const loadAppointments = async () => {
    try {
      const res = await axios.get(`${API}/appointments`);
      setAppointments(res.data);
    } catch (err) { console.error(err); }
  };

  const loadClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (err) { console.error(err); }
  };

  const loadVehiclesForClient = async (clientId) => {
    try {
      const res = await axios.get(`${API}/vehicles`, { params: { client_id: clientId } });
      setVehicles(res.data);
    } catch (err) { setVehicles([]); }
  };

  const segmentEvents = (a) => a.segments.map((sg, i) => ({
    id: `${a.id}-${i}`,
    title: a.client_name || 'Client',
    start: `${sg.date}T${sg.start}`,
    end: `${sg.date}T${sg.end}`,
    backgroundColor: CATEGORY_COLORS[a.category]?.bg || '#3b82f6',
    borderColor: CATEGORY_COLORS[a.category]?.border || '#2563eb',
    extendedProps: { ...a, _seg_start: sg.start, _seg_end: sg.end },
  }));

  const calendarEvents = appointments.flatMap(a => {
    if (a.segments?.length) return segmentEvents(a);
    const isMultiDay = a.end_date && a.end_date !== a.date;
    let start, end;
    if (isMultiDay) {
      // Multi-day: use date-only strings for allDay events
      start = a.date;
      // FullCalendar allDay end is EXCLUSIVE, so add 1 day
      const ed = new Date(a.end_date);
      ed.setDate(ed.getDate() + 1);
      end = ed.toISOString().split('T')[0];
    } else {
      start = `${a.date}T${a.time}`;
      if (a.end_time) {
        end = `${a.date}T${a.end_time}`;
      } else {
        end = null;
      }
    }
    const clientLabel = a.client_name || 'Client';
    const vehicleLabel = a.vehicle_info ? ` - ${a.vehicle_info}` : '';
    return {
      id: a.id,
      title: `${clientLabel}${vehicleLabel}`,
      start,
      end,
      allDay: isMultiDay,
      backgroundColor: CATEGORY_COLORS[a.category]?.bg || '#3b82f6',
      borderColor: CATEGORY_COLORS[a.category]?.border || '#2563eb',
      extendedProps: a,
    };
  }).concat(tasks.map(tk => ({
    id: `task-${tk.id}`,
    title: tk.title,
    start: `${tk.date}T${tk.time}`,
    end: tk.end_time ? `${tk.date}T${tk.end_time}` : null,
    backgroundColor: '#0E1116',
    borderColor: '#FACC15',
    textColor: '#FACC15',
    extendedProps: { ...tk, _kind: 'task' },
  }))).concat((booking && preview ? preview.segments : []).map((sg, i) => ({
    id: `preview-${i}`,
    start: `${sg.date}T${sg.start}`,
    end: `${sg.date}T${sg.end}`,
    backgroundColor: preview.ok ? '#FACC15' : '#FCA5A5',
    borderColor: preview.ok ? '#0E1116' : '#B91C1C',
    textColor: '#0E1116',
    classNames: ['booking-preview'],
    extendedProps: { _kind: 'preview', id: `preview-${i}`, time: sg.start, end_time: sg.end, ok: preview.ok },
  })));

  const handleDateClick = (info) => {
    const date = info.dateStr.split('T')[0];
    const time = info.dateStr.length > 10 ? info.dateStr.slice(11, 16) : '08:00';
    setViewDate(date);
    if (info.view.type === 'dayGridMonth') calendarRef.current?.getApi().changeView('timeGridWeek', date);
    setBooking(b => (b ? { ...b, date, time, nonce: Date.now() } : { date, time, key: Date.now() }));
  };

  const handleEventClick = (info) => {
    const appt = info.event.extendedProps;
    if (appt._kind === 'preview') return;
    if (appt._kind === 'task') { setTaskDialog({ task: appt }); return; }
    setShowDetail(appt);
  };

  const openNewAppt = () => setBooking({ date: viewDate, time: '08:00', key: Date.now() });

  const openEditAppt = async (appt) => {
    {
      try {
        const r = await axios.get(`${API}/agenda/booking/${appt.id}`);
        setShowDetail(null);
        setBooking({ date: appt.date, time: appt.time, key: Date.now(), edit: r.data });
        return;
      } catch { /* fall back to the classic form */ }
    }
    setEditingAppt(appt);
    const client = clients.find(c => c.id === appt.client_id);
    setSelectedClient(client || null);
    if (appt.client_id) loadVehiclesForClient(appt.client_id);
    setForm({
      client_id: appt.client_id, vehicle_id: appt.vehicle_id, date: appt.date,
      time: appt.time, end_date: appt.end_date || '', end_time: appt.end_time || '',
      pickup_date: appt.pickup_date || '',
      pickup_time: appt.pickup_time || '',
      drop_off_time: appt.drop_off_time || '',
      category: appt.category, intervention_type: appt.intervention_type || '',
      description: appt.description || '', insurance_number: appt.insurance_number || '',
      loaner_vehicle: appt.loaner_vehicle || false, loaner_vehicle_rate: appt.loaner_vehicle_rate || '',
      checklist: appt.checklist || {},
      sinistre_numbers: appt.sinistre_numbers || ['', '', ''],
      billing_type: appt.billing_type || 'reparation',
      allocated_hours: appt.allocated_hours || '',
    });
    setPendingFiles([]);
    setShowDetail(null);
    setShowDialog(true);
  };

  const handleCreateNewClient = () => {
    // Save current form to localStorage so we can restore it when coming back
    const formToSave = { ...form };
    delete formToSave.client_id;
    delete formToSave.vehicle_id;
    localStorage.setItem('agenda_pending_form', JSON.stringify(formToSave));
    setShowDialog(false);
    navigate('/admin/clients?from=agenda');
  };

  const selectClient = (client) => {
    setSelectedClient(client);
    setForm(f => ({ ...f, client_id: client.id, vehicle_id: '' }));
    setClientSearch('');
    setFilteredClients([]);
    loadVehiclesForClient(client.id);
  };

  useEffect(() => {
    if (clientSearch.length >= 2) {
      const filtered = clients.filter(c =>
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(clientSearch.toLowerCase()) ||
        (c.phone && c.phone.includes(clientSearch))
      );
      setFilteredClients(filtered.slice(0, 8));
    } else {
      setFilteredClients([]);
    }
  }, [clientSearch, clients]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.date || !form.time) {
      toast.error('Client, date et heure requis');
      return;
    }
    const payload = {
      ...form,
      loaner_vehicle_rate: parseFloat(form.loaner_vehicle_rate) || 0,
      allocated_hours: parseFloat(form.allocated_hours) || 0,
      sinistre_numbers: form.sinistre_numbers.filter(s => s.trim() !== ''),
    };
    try {
      let result;
      if (editingAppt) {
        result = await axios.put(`${API}/appointments/${editingAppt.id}`, payload);
        toast.success('Rendez-vous modifie');
      } else {
        result = await axios.post(`${API}/appointments`, payload);
        toast.success('Rendez-vous cree');
      }
      // Set custom reminder if requested
      const apptId = result.data?.id || editingAppt?.id;
      if (apptId && form.custom_reminder) {
        try {
          await axios.post(`${API}/appointments/${apptId}/reminder`, {
            channel: form.custom_reminder.channel,
            delay: form.custom_reminder.delay,
            message: form.custom_reminder.message,
          });
        } catch { /* don't fail the appointment creation */ }
      }
      // Upload attachments to the associated work order
      const woId = result.data?.work_order_id || editingAppt?.work_order_id;
      if (woId && pendingFiles.length > 0) {
        for (const file of pendingFiles) {
          if (file._fromScan && file._serverFile) {
            // File uploaded via phone scan - just link it
            try {
              await axios.post(`${API}/work-orders/${woId}/attachments/from-scan`, {
                file_id: file._serverFile.id,
                stored_filename: file._serverFile.stored_filename,
                filename: file._serverFile.filename,
                content_type: file._serverFile.content_type,
                size: file._serverFile.size,
              });
            } catch { /* silently continue */ }
          } else {
            // Regular file upload
            const fd = new FormData();
            fd.append('file', file);
            try {
              await axios.post(`${API}/work-orders/${woId}/attachments`, fd);
            } catch { /* silently continue */ }
          }
        }
        toast.success(`${pendingFiles.length} piece(s) jointe(s) ajoutee(s)`);
      }
      setShowDialog(false);
      setPendingFiles([]);
      if (scanPollRef.current) clearInterval(scanPollRef.current);
      setScanSession(null);
      loadAppointments();
    } catch (err) { toast.error('Erreur lors de la sauvegarde'); }
  };

  const handleDelete = async (apptId) => {
    if (!window.confirm('Supprimer ce rendez-vous ?')) return;
    try {
      await axios.delete(`${API}/appointments/${apptId}`);
      toast.success('Rendez-vous supprime');
      setShowDetail(null);
      loadAppointments();
    } catch (err) { toast.error('Erreur'); }
  };

  const handleStatusChange = async (apptId, newStatus) => {
    try {
      await axios.put(`${API}/appointments/${apptId}/status`, { status: newStatus });
      toast.success('Statut mis a jour');
      setShowDetail(null);
      loadAppointments();
    } catch (err) { toast.error('Erreur'); }
  };

  const sendWhatsAppConfirmation = async (appt) => {
    try {
      const res = await axios.post(`${API}/notifications/whatsapp-send`, {
        type: 'confirmation',
        appointment_id: appt.id,
        work_order_id: appt.work_order_id,
      });
      if (res.data.success) {
        toast.success(`Confirmation envoyée par WhatsApp à ${res.data.recipient || 'client'}`);
      } else {
        toast.error(res.data.error || res.data.detail || 'Erreur WhatsApp — Vérifiez la configuration');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur WhatsApp');
    }
  };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900" data-testid="agenda-title">{t('agenda.title')}</h1>
            <p className="text-base text-slate-600 mt-1">{appointments.length} rendez-vous</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="workshop-settings-btn">
              <Clock className="mr-2 w-4 h-4" /> Horaires & postes
            </Button>
            <Button variant="outline" onClick={() => setTaskDialog({ task: null })} data-testid="new-workshop-task-btn">
              <ClipboardList className="mr-2 w-4 h-4" /> Tâche atelier
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/agenda/print-list')} data-testid="print-period-list-btn">
              <ListChecks className="mr-2 w-4 h-4" /> Liste 3 mois
            </Button>
            <Button variant="outline" onClick={() => window.open('/admin/agenda/tv', '_blank')} data-testid="open-tv-planning-btn">
              <Tv className="mr-2 w-4 h-4" /> Écran TV
            </Button>
            <Button onClick={() => navigate(`/admin/agenda/print?date=${viewDate}`)} className="bg-[#0E1116] text-[#FACC15] hover:bg-black" data-testid="print-day-planning-btn">
              <Printer className="mr-2 w-4 h-4" /> Planning du jour (A4)
            </Button>
            <Button onClick={openNewAppt} className="btn-primary" data-testid="new-appointment-btn">
              <Plus className="mr-2 w-4 h-4" /> Nouveau RDV
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4">
          {Object.entries(CATEGORY_COLORS).map(([key, val]) => (
            <div key={key} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: val.bg }} />
              <span className="text-slate-600">{val.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full bg-[#0E1116] ring-2 ring-[#FACC15]" />
            <span className="text-slate-600">Tâche atelier</span>
          </div>
        </div>

        {/* Calendar */}
        <Card className="p-4 rounded-xl agenda-clear" data-testid="agenda-calendar">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            locale="fr"
            firstDay={1}
            events={calendarEvents}
            dateClick={handleDateClick}
            eventContent={(arg) => <AgendaEventContent event={arg.event} view={arg.view} />}
            datesSet={(info) => {
              const start = info.view.currentStart;
              const today = new Date();
              const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              setViewDate(today >= start && today < info.view.currentEnd ? iso(today) : iso(start));
            }}
            dayMaxEvents={false}
            views={{
              dayGridMonth: { dayHeaderFormat: { weekday: 'long' } },
              timeGrid: { dayHeaderFormat: { weekday: 'long', day: 'numeric', month: 'numeric' } },
            }}
            eventDisplay="block"
            slotDuration="00:30:00"
            slotLabelInterval="00:30"
            allDayText="Jour"
            nowIndicator={true}
            eventClick={handleEventClick}
            editable={false}
            selectable={true}
            eventOverlap={true}
            slotEventOverlap={true}
            height="auto"
            displayEventEnd={true}
            nextDayThreshold="00:00:00"
            buttonText={{
              today: "Aujourd'hui",
              month: 'Mois',
              week: 'Semaine',
              day: 'Jour',
            }}
            eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            slotMinTime="07:00:00"
            slotMaxTime="19:00:00"
          />
        </Card>

        {booking && (
          <BookingPanel key={booking.key} initial={booking} onPreview={setPreview}
            onClose={() => { setBooking(null); setPreview(null); }}
            onBooked={() => { setBooking(null); setPreview(null); loadAppointments(); }} />
        )}
        <WorkshopSettingsDialog open={showSettings} onClose={() => setShowSettings(false)} />
        {checkIn && <CheckInDialog key={checkIn.id} appt={checkIn} onClose={() => setCheckIn(null)} onDone={() => { setCheckIn(null); loadAppointments(); }} />}
        {checkOut && <CheckOutDialog key={checkOut.id} appt={checkOut} onClose={() => setCheckOut(null)} onDone={() => { setCheckOut(null); loadAppointments(); }} />}

        <WorkshopTaskDialog
          open={!!taskDialog}
          task={taskDialog?.task}
          defaultDate={viewDate}
          onClose={() => setTaskDialog(null)}
          onSaved={() => { setTaskDialog(null); loadTasks(); }}
        />

        {/* Appointment Detail Dialog */}
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Detail du rendez-vous</DialogTitle>
            </DialogHeader>
            {showDetail && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[showDetail.category]?.bg }} />
                  <span className="text-sm font-medium">{CATEGORY_COLORS[showDetail.category]?.label}</span>
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium ${
                    showDetail.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                    showDetail.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    showDetail.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-slate-100 text-slate-700'
                  }`} data-testid="appointment-status">
                    {STATUS_LABELS[showDetail.status]}
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2"><User className="w-4 h-4 text-slate-400" /><span>{showDetail.client_name}</span></div>
                  {showDetail.vehicle_info && <div className="flex items-center gap-2"><Car className="w-4 h-4 text-slate-400" /><span>{showDetail.vehicle_info}</span></div>}
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span data-testid="appointment-detail-hours">
                      {showDetail.segments?.length ? showDetail.segments.map(s => `${s.date.slice(8, 10)}.${s.date.slice(5, 7)} ${s.start}–${s.end}`).join(' puis ') : <>
                      {showDetail.date} a {showDetail.time}
                      {showDetail.end_date && showDetail.end_date !== showDetail.date
                        ? ` → ${showDetail.end_date}${showDetail.end_time ? ` a ${showDetail.end_time}` : ''}`
                        : showDetail.end_time ? ` - ${showDetail.end_time}` : ''
                      }</>}
                      {showDetail.bay ? ` · Poste ${showDetail.bay}` : ''}
                    </span>
                  </div>
                  {showDetail.drop_off_time && (
                    <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-blue-400" /><span>Depot : {showDetail.drop_off_time}</span></div>
                  )}
                  {(showDetail.pickup_date || showDetail.pickup_time) && (
                    <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-400" /><span>Reprise : {showDetail.pickup_date ? new Date(showDetail.pickup_date).toLocaleDateString('fr-CH') : ''}{showDetail.pickup_date && showDetail.pickup_time ? ' a ' : ''}{showDetail.pickup_time || ''}</span></div>
                  )}
                  {showDetail.intervention_type && (
                    <div className="flex items-center gap-2"><Wrench className="w-4 h-4 text-slate-400" /><span>{showDetail.intervention_type}</span></div>
                  )}
                  {showDetail.description && (
                    <p className="text-slate-600 bg-slate-50 p-3 rounded-lg">{showDetail.description}</p>
                  )}
                  {showDetail.loaner_vehicle && (
                    <div className="flex items-center gap-2"><CarFront className="w-4 h-4 text-amber-500" /><span className="font-medium">Vehicule de pret{showDetail.loaner_vehicle_rate > 0 ? ` (CHF ${showDetail.loaner_vehicle_rate}/jour)` : ''}</span></div>
                  )}
                </div>

                <AppointmentStatusBox key={showDetail.id} appt={showDetail} onCheckIn={() => { setCheckIn(showDetail); setShowDetail(null); }} onCheckOut={() => { setCheckOut(showDetail); setShowDetail(null); }} onChanged={(close) => { loadAppointments(); if (close) setShowDetail(null); }} />

                {/* Status Actions */}
                <div className="flex flex-wrap gap-2">
                  {showDetail.status === 'scheduled' && (
                    <Button size="sm" onClick={() => handleStatusChange(showDetail.id, 'in_progress')} data-testid="start-appointment-btn">
                      Demarrer
                    </Button>
                  )}
                  {showDetail.status === 'in_progress' && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleStatusChange(showDetail.id, 'completed')} data-testid="complete-appointment-btn">
                      Terminer
                    </Button>
                  )}
                  {showDetail.status !== 'cancelled' && showDetail.status !== 'completed' && (
                    <Button size="sm" variant="outline" onClick={() => handleStatusChange(showDetail.id, 'cancelled')} data-testid="cancel-appointment-btn">
                      Annuler
                    </Button>
                  )}
                </div>

                <div className="flex gap-2 pt-2 border-t">
                  <Button variant="outline" size="sm" onClick={() => openEditAppt(showDetail)} data-testid="edit-appointment-btn">
                    Modifier
                  </Button>
                  {showDetail.client_phone && (
                    <Button variant="outline" size="sm" className="text-emerald-600 hover:text-emerald-700" onClick={() => sendWhatsAppConfirmation(showDetail)} data-testid="whatsapp-confirmation-btn">
                      <MessageCircle className="mr-1 w-4 h-4" /> WhatsApp
                    </Button>
                  )}
                  {showDetail.work_order_id && (
                    <Button variant="outline" size="sm" onClick={() => window.location.href = `/admin/work-orders/${showDetail.work_order_id}`} data-testid="view-workorder-btn">
                      <FileText className="mr-1 w-4 h-4" /> Fiche
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 ml-auto" onClick={() => handleDelete(showDetail.id)} data-testid="delete-appointment-btn">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create/Edit Appointment Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingAppt ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Client Selection */}
              <div className="space-y-2">
                <Label>Client *</Label>
                {selectedClient ? (
                  <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">{selectedClient.first_name} {selectedClient.last_name}</p>
                      <p className="text-xs text-slate-500">{selectedClient.phone}</p>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setSelectedClient(null); setForm(f => ({ ...f, client_id: '', vehicle_id: '' })); setVehicles([]); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Rechercher un client..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="pl-10"
                      data-testid="appt-client-search"
                    />
                    {(filteredClients.length > 0 || clientSearch.length >= 2) && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredClients.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm"
                            onClick={() => selectClient(c)}
                            data-testid={`select-client-${c.id}`}
                          >
                            <span className="font-medium">{c.first_name} {c.last_name}</span>
                            {c.phone && <span className="text-slate-400 ml-2">{c.phone}</span>}
                          </button>
                        ))}
                        {clientSearch.length >= 2 && filteredClients.length === 0 && (
                          <div className="px-4 py-2 text-sm text-slate-500">Aucun client trouve</div>
                        )}
                        <button
                          type="button"
                          className="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-sm border-t border-slate-100 flex items-center gap-2 text-blue-600 font-medium"
                          onClick={handleCreateNewClient}
                          data-testid="create-client-from-agenda-btn"
                        >
                          <UserPlus className="w-4 h-4" />
                          Creer un nouveau client
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Vehicle Selection */}
              {selectedClient && (
                <div className="space-y-2">
                  <Label>Véhicule</Label>
                  {vehicles.length > 0 ? (
                    <Select value={form.vehicle_id} onValueChange={(v) => setForm(f => ({ ...f, vehicle_id: v }))}>
                      <SelectTrigger data-testid="appt-vehicle-select">
                        <SelectValue placeholder="Sélectionner un véhicule" />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicles.map(v => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.brand} {v.model} - {v.plate}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">Aucun véhicule pour ce client</p>
                  )}
                </div>
              )}

              {/* Date/Time - Outlook style */}
              <div className="bg-slate-50 p-3 rounded-lg space-y-3">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Planification</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Debut - Date *</Label>
                    <Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} required data-testid="appt-date" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Debut - Heure *</Label>
                    <Select value={form.time} onValueChange={(v) => setForm(f => ({ ...f, time: v }))} data-testid="appt-time">
                      <SelectTrigger><SelectValue placeholder="Heure" /></SelectTrigger>
                      <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Fin - Date</Label>
                    <Input type="date" value={form.end_date} onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))} data-testid="appt-end-date" placeholder="Meme jour si vide" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Fin - Heure</Label>
                    <Select value={form.end_time} onValueChange={(v) => setForm(f => ({ ...f, end_time: v }))} data-testid="appt-end-time">
                      <SelectTrigger><SelectValue placeholder="--:--" /></SelectTrigger>
                      <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Reprise - Date</Label>
                    <Input type="date" value={form.pickup_date} onChange={(e) => setForm(f => ({ ...f, pickup_date: e.target.value }))} data-testid="appt-pickup-date" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Reprise - Heure</Label>
                    <Select value={form.pickup_time} onValueChange={(v) => setForm(f => ({ ...f, pickup_time: v }))} data-testid="appt-pickup">
                      <SelectTrigger><SelectValue placeholder="--:--" /></SelectTrigger>
                      <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {form.end_date && form.end_date !== form.date && (
                  <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                    Vehicule sur {Math.ceil((new Date(form.end_date) - new Date(form.date)) / 86400000) + 1} jours
                  </p>
                )}
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label>Categorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="appt-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mecanique"><div className="flex items-center gap-2"><Wrench className="w-4 h-4" /> Mecanique</div></SelectItem>
                    <SelectItem value="carrosserie"><div className="flex items-center gap-2"><PaintBucket className="w-4 h-4" /> Carrosserie</div></SelectItem>
                    <SelectItem value="assurance"><div className="flex items-center gap-2"><Shield className="w-4 h-4" /> Assurance</div></SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Insurance number (only for assurance) */}
              {form.category === 'assurance' && (
                <div className="space-y-2">
                  <Label>N° de sinistre</Label>
                  <Input value={form.insurance_number} onChange={(e) => setForm(f => ({ ...f, insurance_number: e.target.value }))} data-testid="appt-insurance" />
                </div>
              )}

              {/* Billing type + Allocated time */}
              <div className="bg-slate-50 p-3 rounded-lg space-y-3">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Type & Temps</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Type de fiche *</Label>
                    <div className="flex gap-2">
                      <button type="button"
                        onClick={() => setForm(f => ({ ...f, billing_type: 'reparation' }))}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold border-2 transition-all ${
                          form.billing_type === 'reparation'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                        }`}
                        data-testid="billing-type-reparation"
                      >
                        Réparation
                      </button>
                      <button type="button"
                        onClick={() => setForm(f => ({ ...f, billing_type: 'devis' }))}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold border-2 transition-all ${
                          form.billing_type === 'devis'
                            ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-amber-300'
                        }`}
                        data-testid="billing-type-devis"
                      >
                        Devis
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Temps alloué (heures)</Label>
                    <Input
                      type="number"
                      step="0.25"
                      min="0"
                      value={form.allocated_hours}
                      onChange={(e) => setForm(f => ({ ...f, allocated_hours: e.target.value }))}
                      placeholder="Ex: 2.5"
                      data-testid="allocated-hours-input"
                    />
                  </div>
                </div>
              </div>

              {/* Loaner vehicle */}
              <div className="space-y-3 bg-slate-50 p-3 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="appt-loaner-vehicle">
                  <Checkbox
                    checked={form.loaner_vehicle}
                    onCheckedChange={(checked) => setForm(f => ({ ...f, loaner_vehicle: !!checked }))}
                  />
                  <CarFront className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">Véhicule de prêt</span>
                </label>
                {form.loaner_vehicle && (
                  <div className="space-y-2 pl-8">
                    <Label className="text-xs">Tarif journalier (CHF)</Label>
                    <Input
                      type="number"
                      value={form.loaner_vehicle_rate}
                      onChange={(e) => setForm(f => ({ ...f, loaner_vehicle_rate: e.target.value }))}
                      placeholder="0.00"
                      className="w-32"
                      data-testid="appt-loaner-rate"
                    />
                  </div>
                )}
              </div>

              {/* Checklist travaux - identique à la fiche de travail */}
              <div className="space-y-3 bg-slate-50 p-3 rounded-lg" data-testid="appt-checklist">
                <Label className="text-sm font-bold">Demandes du client</Label>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(CHECKLIST_ITEMS).map(([cat, items]) => (
                    <div key={cat} className="space-y-2">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{cat}</p>
                      {items.map(item => (
                        <label key={item.key} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`appt-check-${item.key}`}>
                          <Checkbox
                            checked={!!form.checklist[item.key]}
                            onCheckedChange={(checked) => setForm(f => ({
                              ...f,
                              checklist: { ...f.checklist, [item.key]: !!checked }
                            }))}
                          />
                          <span>{item.label}</span>
                        </label>
                      ))}
                      {cat === 'assurance' && (
                        <div className="mt-3 space-y-1.5">
                          <Label className="text-xs text-slate-500">N° de sinistre</Label>
                          {[0, 1, 2].map(idx => (
                            <Input key={idx}
                              value={form.sinistre_numbers[idx] || ''}
                              onChange={(e) => {
                                const updated = [...form.sinistre_numbers];
                                updated[idx] = e.target.value;
                                setForm(f => ({ ...f, sinistre_numbers: updated }));
                              }}
                              placeholder={`Sinistre ${idx + 1}...`}
                              className="h-8 text-sm"
                              data-testid={`appt-sinistre-${idx}`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description / Demande client</Label>
                <Textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={3} data-testid="appt-description" />
              </div>

              {/* Pièces jointes */}
              <div className="space-y-2 bg-slate-50 p-3 rounded-lg">
                <Label className="text-sm font-bold flex items-center gap-2">
                  <Paperclip className="w-4 h-4" /> Pièces jointes
                </Label>
                <p className="text-xs text-slate-500">Photos, documents, rapports d'expertise - visibles dans la fiche atelier</p>
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      className="hidden"
                      data-testid="appt-attachment-input"
                      onChange={(e) => {
                        if (e.target.files) {
                          setPendingFiles(prev => [...prev, ...Array.from(e.target.files)]);
                        }
                      }}
                    />
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-slate-200 hover:bg-slate-100 rounded-md transition-colors cursor-pointer">
                      <Upload className="w-3.5 h-3.5" /> Ajouter un fichier
                    </span>
                  </label>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors"
                    data-testid="scan-qr-attachment-btn"
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/attachment-scan-sessions`, { context: 'appointment' });
                        setScanSession(res.data);
                        // Poll for uploaded files
                        if (scanPollRef.current) clearInterval(scanPollRef.current);
                        scanPollRef.current = setInterval(async () => {
                          try {
                            const poll = await axios.get(`${API}/attachment-scan-sessions/${res.data.id}`);
                            if (poll.data.files && poll.data.files.length > 0) {
                              // Convert server files to File-like objects for pendingFiles
                              const newFiles = poll.data.files.map(f => ({
                                name: f.filename,
                                size: f.size,
                                _serverFile: f,
                                _fromScan: true,
                              }));
                              setPendingFiles(prev => {
                                const existingIds = prev.filter(p => p._serverFile).map(p => p._serverFile.id);
                                const fresh = newFiles.filter(nf => !existingIds.includes(nf._serverFile.id));
                                return fresh.length > 0 ? [...prev, ...fresh] : prev;
                              });
                            }
                          } catch (err) { /* ignore poll errors */ }
                        }, 2000);
                      } catch (err) { toast.error('Erreur creation session scan'); }
                    }}
                  >
                    <Smartphone className="w-3.5 h-3.5" /> Scanner par telephone
                  </button>
                </div>

                {/* QR Code scan modal */}
                {scanSession && (
                  <div className="mt-3 p-4 bg-white rounded-xl border-2 border-blue-200 text-center">
                    <p className="text-sm font-medium text-slate-700 mb-2">Scannez ce QR code avec votre telephone</p>
                    <img src={scanSession.qr_code} alt="QR Code" className="w-40 h-40 mx-auto" data-testid="attachment-qr-code" />
                    <p className="text-xs text-slate-500 mt-2">En attente des photos...</p>
                    <div className="flex justify-center mt-2">
                      <button
                        type="button"
                        className="text-sm text-slate-500 hover:text-slate-700 underline"
                        onClick={() => {
                          if (scanPollRef.current) clearInterval(scanPollRef.current);
                          setScanSession(null);
                        }}
                        data-testid="close-qr-scan"
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                )}
                {pendingFiles.length > 0 && (
                  <div className="space-y-1 mt-2">
                    {pendingFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm bg-white px-2 py-1 rounded border">
                        <Paperclip className="w-3 h-3 text-slate-400" />
                        <span className="flex-1 truncate">{file.name}</span>
                        <span className="text-xs text-slate-400">{(file.size / 1024).toFixed(0)} Ko</span>
                        <button type="button" onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rappel automatique */}
              <div className="space-y-2 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                <Label className="text-sm font-bold flex items-center gap-2">
                  <Bell className="w-4 h-4 text-blue-600" /> Rappel automatique
                </Label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    checked={!!form.custom_reminder}
                    onCheckedChange={(checked) => setForm(f => ({
                      ...f,
                      custom_reminder: checked ? { channel: 'both', delay: '24h', message: '' } : null
                    }))}
                    data-testid="appt-reminder-toggle"
                  />
                  <span>Envoyer un rappel au client</span>
                </label>
                {form.custom_reminder && (
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <div>
                      <Label className="text-xs">Canal</Label>
                      <Select value={form.custom_reminder.channel} onValueChange={v => setForm(f => ({ ...f, custom_reminder: { ...f.custom_reminder, channel: v } }))}>
                        <SelectTrigger data-testid="appt-reminder-channel"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="whatsapp">WhatsApp</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="both">WhatsApp + Email</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Delai avant RDV</Label>
                      <Select value={form.custom_reminder.delay} onValueChange={v => setForm(f => ({ ...f, custom_reminder: { ...f.custom_reminder, delay: v } }))}>
                        <SelectTrigger data-testid="appt-reminder-delay"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1h">1 heure</SelectItem>
                          <SelectItem value="2h">2 heures</SelectItem>
                          <SelectItem value="12h">12 heures</SelectItem>
                          <SelectItem value="24h">24 heures</SelectItem>
                          <SelectItem value="48h">48 heures</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Message personnalise (optionnel)</Label>
                      <Input
                        placeholder="Ex: N'oubliez pas vos documents..."
                        value={form.custom_reminder.message || ''}
                        onChange={e => setForm(f => ({ ...f, custom_reminder: { ...f.custom_reminder, message: e.target.value } }))}
                        data-testid="appt-reminder-message"
                      />
                    </div>
                  </div>
                )}
              </div>

              <Button type="submit" className="w-full btn-primary" data-testid="submit-appointment-btn">
                {editingAppt ? 'Enregistrer' : 'Creer le rendez-vous'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AgendaPage;
